import { FormModelChangeHandler } from "@/types/forms/types";
import {
  MovementType,
  PriceCalculatorFormData,
  PriceCalculatorFormDataDefaultState,
} from "@/features/price-calculator/types/types";
import { PriceCalculatorVehicleTypeSelectQuery } from "@/gql/graphql";
import { useQuery } from "@apollo/client";
import { Select, SelectProps } from "@stenajs-webui/select";
import { gql } from "@apollo/client";
import * as React from "react";
import { useCallback, useMemo } from "react";

type VehicleTypes =
  PriceCalculatorVehicleTypeSelectQuery["productPrice"]["vehicleType"]["all"];

const setInitialValuesByMovementType = (
  vehicleTypes: VehicleTypes,
  movementType: MovementType,
  onChangeFormData: FormModelChangeHandler<PriceCalculatorFormData>,
  formData: PriceCalculatorFormData,
  setDefaultFormState: React.Dispatch<
    React.SetStateAction<PriceCalculatorFormDataDefaultState>
  >
) => {
  const isSectional = vehicleTypes.filter(
    (a) => a.isMultilegBookable === false
  )[0];
  const isMultilegBookable = vehicleTypes.filter(
    (a) => a.isMultilegBookable === true
  )[0];
  const valuesByMovementType =
    movementType === MovementType.SECTIONAL ? isSectional : isMultilegBookable;

  onChangeFormData("vehicleType", valuesByMovementType.code);
  onChangeFormData(
    "vehicleShortDescription",
    valuesByMovementType.shortDescription
  );
  onChangeFormData("vehicleLength", String(valuesByMovementType.length) ?? "");
  onChangeFormData("vehicleWidth", String(valuesByMovementType.width) ?? "");
  onChangeFormData("vehicleWeight", String(valuesByMovementType.weight) ?? "");
  onChangeFormData("passengersAdults", valuesByMovementType.noOfDrivers ?? 0);

  setDefaultFormState({
    ...formData,
    vehicleType: valuesByMovementType.code,
    vehicleShortDescription: valuesByMovementType.shortDescription,
    vehicleLength: String(valuesByMovementType.length) ?? "",
    vehicleWidth: String(valuesByMovementType.width) ?? "",
    vehicleWeight: String(valuesByMovementType.weight) ?? "",
    passengersAdults: valuesByMovementType.noOfDrivers ?? 0,
    isInitialized: true,
  });
};

interface Props extends Omit<SelectProps<any>, "value" | "onChange"> {
  formData: PriceCalculatorFormData;
  onChangeFormData: FormModelChangeHandler<PriceCalculatorFormData>;
  setDefaultFormState: React.Dispatch<
    React.SetStateAction<PriceCalculatorFormDataDefaultState>
  >;
  defaultFormState: PriceCalculatorFormDataDefaultState;
}

const query = gql`
  query PriceCalculatorVehicleTypeSelect {
    productPrice {
      vehicleType {
        all {
          id
          shortDescription
          code
          length
          noOfDrivers
          weight
          width
          isMultilegBookable
        }
      }
    }
  }
`;

export const PriceCalculatorVehicleSelect: React.FC<Props> = ({
  formData,
  onChangeFormData,
  setDefaultFormState,
  defaultFormState,
  ...selectProps
}) => {
  const { data, loading } = useQuery<PriceCalculatorVehicleTypeSelectQuery>(
    query,
    {
      onCompleted(data) {
        if (!defaultFormState.isInitialized) {
          setInitialValuesByMovementType(
            data.productPrice.vehicleType.all,
            formData.movementType,
            onChangeFormData,
            formData,
            setDefaultFormState
          );
        }
      },
    }
  );
  const all = useMemo(() => {
    return data?.productPrice.vehicleType.all ?? [];
  }, [data]);

  const onChangeVehicle = useCallback(
    (value: string) => {
      const selectedVehicle = all.find((vehicle) => vehicle.id === value);
      if (selectedVehicle) {
        onChangeFormData("vehicleType", value);
        onChangeFormData(
          "vehicleShortDescription",
          selectedVehicle.shortDescription
        );
        onChangeFormData("vehicleLength", String(selectedVehicle.length) ?? "");
        onChangeFormData("vehicleWidth", String(selectedVehicle.width) ?? "");
        onChangeFormData("vehicleWeight", String(selectedVehicle.weight) ?? "");
        onChangeFormData("passengersAdults", selectedVehicle.noOfDrivers ?? 0);
      }
    },
    [all, onChangeFormData]
  );

  const options = useMemo(() => {
    if (formData.movementType === MovementType.MULTILEG) {
      return all.map((s) => {
        if (s.isMultilegBookable) {
          return {
            label: s.shortDescription,
            value: s.id,
          };
        }
        return {};
      });
    } else {
      return all.map((s) => ({
        label: s.shortDescription,
        value: s.id,
      }));
    }
  }, [all, formData]);

  const filteredOptions = options.filter((opt) => {
    return Object.keys(opt).length !== 0 && opt.constructor === Object;
  });

  const initialValue = useMemo(() => {
    const valueExist = filteredOptions.some(
      (fo) => fo.value === formData.vehicleType
    );

    if (valueExist) {
      return formData.vehicleType;
    }

    if (filteredOptions.length === 0) {
      return "";
    }

    return filteredOptions[0].value;
  }, [formData.vehicleType, filteredOptions]);

  const selected = useMemo(() => {
    const selectedValue = filteredOptions?.find(
      (o) => o?.value === initialValue
    );
    if (!selectedValue) {
      return "";
    }

    return selectedValue;
  }, [filteredOptions, initialValue]);

  return (
    <Select
      isLoading={loading}
      options={filteredOptions}
      value={selected || null}
      onChange={(v: any) => onChangeVehicle?.(v.value)}
      {...selectProps}
    />
  );
};
